import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Typography,
  Button,
  Divider,
  Grid,
  Paper,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { MACHINE_TYPES } from "../../../variables/machineVariables";
import exerciseConstants from "constants/Exercises/exerciseConstants";
import { GRAMMAR_TYPES } from "variables/grammarVariables";
import useExerciseFormValidation from "../hooks/useExerciseFormValidation";

const useStyles = makeStyles(() => ({
  formContainer: {
    backgroundColor: "#ffffff !important",
    height: "100%",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff !important",
      "& fieldset": {
        borderColor: "#4a5568 !important",
        borderWidth: "1.5px !important",
      },
      "&:hover fieldset": {
        borderColor: "#3f51b5 !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3f51b5 !important",
        borderWidth: "2px !important",
      },
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      backgroundColor: "#ffffff !important",
      padding: "0 8px !important",
      color: "#3f51b5 !important",
      fontWeight: "700 !important",
      zIndex: 2,
    },
    "& .MuiOutlinedInput-input": {
      color: "#1a202c !important",
      "&::placeholder": {
        color: "#718096 !important",
        opacity: "1 !important",
        fontSize: "0.9rem",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      "& legend": {
        width: "0.01px !important",
      },
    },
  },
  validationPaper: {
    padding: "24px",
    backgroundColor: "#f8fafc !important",
    border: "2px dashed #3f51b5 !important",
    borderRadius: "8px",
    marginTop: "16px",
  },
  sectionHeader: {
    fontWeight: 800,
    color: "#3f51b5",
    marginBottom: "16px",
    textTransform: "uppercase",
    fontSize: "0.75rem",
  },
}));

/**
 * Componente de formulário para criação e edição de exercícios de Linguagens Formais e Autômatos.
 * Suporta internacionalização e diferentes tipos de simuladores.
 * * @author Willian
 * @param {Object} props Propriedades do componente.
 * @param {Object} props.current Objeto contendo os dados do exercício atual.
 * @param {Function} props.setCurrent Função para atualizar os dados do exercício no estado pai.
 * @param {string} props.type Tipo de exercício (ex: 'MACHINE' ou 'GRAMMAR').
 * @param {string} props.language Idioma da interface ('pt' ou 'en').
 * @param {Function} props.onSave Callback executado ao salvar o formulário.
 * @param {Function} props.onCancel Callback executado ao cancelar a operação.
 * @returns {JSX.Element} O componente ExerciseForm renderizado.
 */
export default function ExerciseForm({
  current,
  setCurrent,
  type,
  language,
  onSave,
  onCancel,
}) {
  const classes = useStyles();
  const { errors, validate, clearError } = useExerciseFormValidation();

  const labels = exerciseConstants.form.labels;
  const placeholders = exerciseConstants.form.placeholders;
  const buttons = exerciseConstants.buttons;
  const formText = exerciseConstants.form;
  const machineOptions = exerciseConstants.machineTypes;
  const grammarOptions = exerciseConstants.grammarTypes;
  const formErrors = exerciseConstants.form.errors;
  const LAMBDA_SYMBOL = "λ";
  const EMPTY_INPUT_TRIGGER = ".";

  let initialAccepted = "";
  let initialRejected = "";

  if (current) {
    if (Array.isArray(current.acceptedWords)) {
      initialAccepted = current.acceptedWords.join(", ");
    }
    if (Array.isArray(current.rejectedWords)) {
      initialRejected = current.rejectedWords.join(", ");
    }
  }

  const [wordsState, setWordsState] = useState({
    accepted: initialAccepted,
    rejected: initialRejected,
  });

  const isMachine = type === "MACHINE";
  const isGrammar = type === "GRAMMAR";

  let typeLabel = "";
  if (isMachine) {
    typeLabel = exerciseConstants.title.machine[language];
  } else if (isGrammar) {
    typeLabel = exerciseConstants.title.grammar[language];
  } else {
    typeLabel = exerciseConstants.title.regularExpressions[language];
  }

  /**
   * Sincroniza o estado local de palavras aceitas/rejeitadas quando o objeto current é alterado.
   */
  useEffect(() => {
    if (current) {
      let acceptedStr = "";
      let rejectedStr = "";

      if (Array.isArray(current.acceptedWords)) {
        acceptedStr = current.acceptedWords.join(", ");
      }

      if (Array.isArray(current.rejectedWords)) {
        rejectedStr = current.rejectedWords.join(", ");
      }

      setWordsState({
        accepted: acceptedStr,
        rejected: rejectedStr,
      });
    }
  }, [current]);

  /**
   * Define o tipo padrão de autômato (AF) caso não esteja definido para exercícios do tipo MACHINE.
   */
  useEffect(() => {
    if (isMachine) {
      if (current) {
        if (!current.automatonType) {
          setCurrent({ ...current, automatonType: MACHINE_TYPES.AF });
        }
      }
    }
  }, [type, current, setCurrent, isMachine]);

  /**
   * Gerencia as mudanças nos campos de texto do formulário.
   * @param {string} field Nome do campo no objeto principal.
   * @param {any} value Novo valor a ser atribuído.
   * @param {string} [subField] (Opcional) Nome do subcampo para objetos aninhados (ex: títulos traduzidos).
   */
  const handleChange = (field, value, subField) => {
    if (subField) {
      let base = {};
      if (current[field]) {
        base = current[field];
      }
      setCurrent({ ...current, [field]: { ...base, [subField]: value } });
    } else {
      setCurrent({ ...current, [field]: value });
    }
  };

  /**
   * Converte uma string separada por vírgulas em um array de strings limpas.
   * @param {string} val String de entrada.
   * @returns {string[]} Array de palavras.
   */
  const handleInternalSave = () => {
    const isValid = validate(current, wordsState);
    if (isValid === true) {
      const format = (val) => {
        if (!val) {
          return [];
        }
        return val
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s !== "");
      };
      const payload = {
        ...current,
        type,
        acceptedWords: format(wordsState.accepted),
        rejectedWords: format(wordsState.rejected),
      };

      if (isGrammar === true) {
        payload.grammarType = valGrammarType;
      }

      if (isMachine === true) {
        payload.automatonType = valAutomatonType;
      }

      onSave(payload);
    }
  };

  /**
   * Substitui o gatilho de entrada pelo símbolo Lambda definido.
   * @param {string} value O texto digitado pelo usuário.
   * @returns {string} O texto processado.
   */
  const handleLambdaReplacement = (value) => {
    if (value.includes(EMPTY_INPUT_TRIGGER)) {
      return value.split(EMPTY_INPUT_TRIGGER).join(LAMBDA_SYMBOL);
    }
    return value;
  };

  const defaultTextFieldProps = {
    fullWidth: true,
    variant: "outlined",
    margin: "normal",
    className: classes.textField,
    InputLabelProps: { shrink: true },
  };

  let valAuthor = "";
  if (current && current.author) {
    valAuthor = current.author;
  }

  let valTitlePt = "";
  if (current && current.title && current.title.pt) {
    valTitlePt = current.title.pt;
  }

  let valTitleEn = "";
  if (current && current.title && current.title.en) {
    valTitleEn = current.title.en;
  }

  let valDescPt = "";
  if (current && current.description && current.description.pt) {
    valDescPt = current.description.pt;
  }

  let valDescEn = "";
  if (current && current.description && current.description.en) {
    valDescEn = current.description.en;
  }

  let valAutomatonType = MACHINE_TYPES.AF;
  if (current && current.automatonType) {
    valAutomatonType = current.automatonType;
  }

  let valGrammarType = GRAMMAR_TYPES.RG;
  if (current && current.grammarType) {
    valGrammarType = current.grammarType;
  }
  /**
   * Renderiza apenas o seletor de tipo (Máquina ou Gramática)
   */
  function renderTypeSelector() {
    var options = null;
    var currentValue = "";
    var fieldName = "";
    var labelText = "";

    if (isMachine === true) {
      options = machineOptions;
      currentValue = valAutomatonType;
      fieldName = "automatonType";
      labelText = labels.simulator[language];
    } else if (isGrammar === true) {
      options = grammarOptions;
      currentValue = valGrammarType;
      fieldName = "grammarType";
      labelText = labels.simulator[language];
    } else {
      return null;
    }

    return (
      <Grid item xs={12} sm={5}>
        <TextField
          {...defaultTextFieldProps}
          id={"ex-" + fieldName}
          name={fieldName}
          select
          label={labelText}
          value={currentValue}
          onChange={function (e) {
            handleChange(fieldName, e.target.value);
          }}
          SelectProps={{ native: true }}
        >
          {Object.keys(options).map(function (key) {
            if (options[key]) {
              if (options[key][language]) {
                return (
                  <option key={key} value={key}>
                    {options[key][language]}
                  </option>
                );
              }
            }
            return null;
          })}
        </TextField>
      </Grid>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      className={classes.formContainer}
    >
      {/* CABEÇALHO */}
      <Box display="flex" alignItems="center" p={2}>
        <IconButton onClick={onCancel} size="small" style={{ marginRight: 10 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" style={{ fontWeight: 700 }}>
          {formText.editorTitle[language]} {typeLabel}
        </Typography>
      </Box>

      <Divider />

      {/* CORPO DO FORMULÁRIO */}
      <Box p={3} style={{ flex: 1, overflowY: "auto" }}>
        <Grid container spacing={3}>
          {/* CAMPO AUTOR */}
          <Grid item xs={12} sm={isMachine || isGrammar === true ? 7 : 12}>
            <TextField
              {...defaultTextFieldProps}
              id="ex-author"
              name="author"
              label={labels.author[language]}
              placeholder={placeholders.author[language]}
              value={valAuthor}
              onChange={function (e) {
                handleChange("author", e.target.value);
              }}
            />
          </Grid>

          {/* SELETOR DINÂMICO */}
          {renderTypeSelector()}

          {/* TÍTULOS */}
          <Grid item xs={12} sm={6}>
            <TextField
              {...defaultTextFieldProps}
              id="ex-title-pt"
              name="title_pt"
              label={labels.titlePt[language]}
              placeholder={placeholders.titlePt[language]}
              value={valTitlePt}
              error={errors.titlePt === true}
              helperText={
                errors.titlePt === true ? formErrors.required[language] : ""
              }
              onChange={function (e) {
                handleChange("title", e.target.value, "pt");
                clearError("titlePt");
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              {...defaultTextFieldProps}
              id="ex-title-en"
              name="title_en"
              label={labels.titleEn[language]}
              placeholder={placeholders.titleEn[language]}
              value={valTitleEn}
              error={errors.titleEn === true}
              helperText={
                errors.titleEn === true ? formErrors.required[language] : ""
              }
              onChange={function (e) {
                handleChange("title", e.target.value, "en");
                clearError("titleEn");
              }}
            />
          </Grid>

          {/* DESCRIÇÕES */}
          <Grid item xs={12}>
            <TextField
              {...defaultTextFieldProps}
              id="ex-desc-pt"
              name="desc_pt"
              label={labels.descPt[language]}
              placeholder={placeholders.descPt[language]}
              multiline
              minRows={4}
              value={valDescPt}
              error={errors.descPt === true}
              helperText={
                errors.descPt === true ? formErrors.required[language] : ""
              }
              onChange={function (e) {
                handleChange("description", e.target.value, "pt");
                clearError("descPt");
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...defaultTextFieldProps}
              id="ex-desc-en"
              name="desc_en"
              label={labels.descEn[language]}
              placeholder={placeholders.descEn[language]}
              error={errors.descEn === true}
              helperText={
                errors.descEn === true ? formErrors.required[language] : ""
              }
              multiline
              minRows={3}
              value={valDescEn}
              onChange={function (e) {
                handleChange("description", e.target.value, "en");
                clearError("descEn");
              }}
            />
          </Grid>

          {/* VALIDAÇÃO DE PALAVRAS */}
          <Grid item xs={12}>
            <Paper elevation={0} className={classes.validationPaper}>
              <Typography className={classes.sectionHeader}>
                {formText.validation[language]}
              </Typography>

              <TextField
                {...defaultTextFieldProps}
                id="ex-accepted"
                name="accepted_words"
                label={labels.accepted[language]}
                placeholder={placeholders.acceptedHelp[language]}
                value={wordsState.accepted}
                error={errors.accepted === true}
                helperText={
                  errors.accepted === true ? formErrors.minWords[language] : ""
                }
                onChange={function (e) {
                  const processedValue = handleLambdaReplacement(
                    e.target.value
                  );

                  setWordsState({
                    ...wordsState,
                    accepted: processedValue,
                  });

                  clearError("accepted");
                }}
              />

              {/* PALAVRAS REJEITADAS */}
              <Box mt={2}>
                <TextField
                  {...defaultTextFieldProps}
                  id="ex-rejected"
                  name="rejected_words"
                  label={labels.rejected[language]}
                  placeholder={placeholders.rejectedHelp[language]}
                  value={wordsState.rejected}
                  error={errors.rejected === true}
                  helperText={
                    errors.rejected === true
                      ? formErrors.minWords[language]
                      : ""
                  }
                  onChange={function (e) {
                    const processedValue = handleLambdaReplacement(
                      e.target.value
                    );

                    setWordsState({
                      ...wordsState,
                      rejected: processedValue,
                    });

                    if (errors.rejected === true) {
                      clearError("rejected");
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {/* RODAPÉ */}
      <Box
        display="flex"
        justifyContent="flex-end"
        p={2}
        style={{ gap: "12px" }}
      >
        <Button
          onClick={onCancel}
          variant="text"
          style={{ fontWeight: "bold", color: "#666" }}
        >
          {buttons.cancel[language]}
        </Button>
        <Button
          onClick={handleInternalSave}
          variant="contained"
          color="primary"
          style={{ fontWeight: "bold", padding: "10px 30px" }}
        >
          {buttons.save[language]} {typeLabel.toUpperCase()}
        </Button>
      </Box>
    </Box>
  );
}
